/**
 * NavBallCard - Attitude visualization (KSP-style NavBall)
 *
 * Visual sphere showing spacecraft orientation with markers for
 * prograde, normal, radial vectors and target alignment.
 *
 * Astro UX Compliance:
 * - Uses official Astro status colors via theme tokens
 * - Sentence-case labels
 * - Tabular numbers for data
 * - Roboto/Roboto Mono typography
 * - 4px-based spacing
 */

import React, { useMemo } from "react";
import type { AttitudeData, PointingMode } from "../types";
import { useTheme } from "../theme";
import { safeAccentText, type StatusLevel } from "../utils";

export interface NavBallCardProps {
  /** Attitude data from API */
  attitude: AttitudeData;
  /** Current pointing mode */
  pointingMode?: PointingMode;
  /** Target name for display */
  targetName?: string;
  /** Compact mode */
  compact?: boolean;
  /** Custom class name */
  className?: string;
}

export const NavBallCard: React.FC<NavBallCardProps> = ({
  attitude,
  pointingMode = "nadir",
  targetName,
  compact = false,
  className = "",
}) => {
  const { tokens, theme } = useTheme();
  const isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  // Use transparent background with blur for transparent themes (matching Timeline)
  const cardBg = isTransparentTheme
    ? "transparent"
    : tokens.colors.background.surface;
  const cardGlass = isTransparentTheme
    ? {
        backdropFilter: "blur(12px)" as const,
        WebkitBackdropFilter: "blur(12px)" as const,
      }
    : {};
  const { roll, pitch, yaw } = attitude.eulerAngles;
  const pointingError = attitude.pointingError ?? 0;

  // Calculate visual transforms
  const horizonTransform = useMemo(() => {
    const pitchClamp = Math.max(-45, Math.min(45, pitch));
    const rollClamp = Math.max(-180, Math.min(180, roll));
    return {
      transform: `rotate(${rollClamp}deg) translateY(${pitchClamp * 1.5}%)`,
    };
  }, [pitch, roll]);

  // Format angle for display (Astro: tabular numbers)
  const formatAngle = (angle: number, prefix: string): string => {
    const sign = angle >= 0 ? "+" : "";
    return `${prefix}: ${sign}${angle.toFixed(1)}°`;
  };

  // Medium-soft status colors (readable, not too pale)
  const statusColors: Record<StatusLevel, string> = {
    normal: "#6ed86e",
    caution: "#ffd54f",
    serious: "#ffa726",
    critical: "#e57373",
    off: "#90a4ae",
    standby: "#4fc3f7",
  };

  const errorColor = useMemo(() => {
    if (pointingError < 0.5) return statusColors.normal;
    if (pointingError < 2) return statusColors.caution;
    return statusColors.critical;
  }, [pointingError]);

  const sizeStyle: React.CSSProperties = compact
    ? { width: "96px", height: "96px" }
    : { width: "160px", height: "160px" };

  // Astro UX compliant card styling (transparent theme: glass background)
  const cardStyle: React.CSSProperties = {
    backgroundColor: cardBg,
    ...cardGlass,
    border: "none",
    borderRadius: tokens.borderRadius.lg,
    padding: tokens.spacing.md,
    fontFamily: tokens.typography.fontFamily.primary,
    color: tokens.colors.text.primary,
  };

  return (
    <div className={className} style={cardStyle}>
      {!compact && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: tokens.spacing.md,
          }}
        >
          <div>
            {/* Astro: Sentence-case labels */}
            <h3
              style={{
                fontWeight: tokens.typography.fontWeight.medium,
                fontSize: tokens.typography.fontSize.md,
                color: tokens.colors.text.primary,
                margin: 0,
              }}
            >
              Navball
            </h3>
            <p
              style={{
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
                margin: 0,
                marginTop: tokens.spacing.xs,
              }}
            >
              {pointingMode
                ? pointingMode.charAt(0).toUpperCase() +
                  pointingMode.slice(1).toLowerCase()
                : "Unknown"}{" "}
              pointing
            </p>
          </div>
          {/* Astro: Tabular numbers for data */}
          <span
            style={{
              fontSize: tokens.typography.fontSize.xs,
              fontFamily: tokens.typography.fontFamily.mono,
              fontVariantNumeric: "tabular-nums",
              color: errorColor,
            }}
          >
            Δ {pointingError.toFixed(2)}°
          </span>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ position: "relative", ...sizeStyle }}>
          {/* Outer ring */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `4px solid ${tokens.colors.border.muted}`,
              overflow: "hidden",
              backgroundColor: tokens.colors.background.base,
            }}
          >
            {/* Horizon gradient (day/night split) */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                transition: "transform 0.3s ease-out",
                ...horizonTransform,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: "-50%",
                  transformOrigin: "center",
                  background:
                    "linear-gradient(to bottom, #1e3a5f 0%, #1e3a5f 48%, #78350f 52%, #78350f 100%)",
                }}
              />

              {/* Grid lines */}
              <svg
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0.2,
                }}
                viewBox="0 0 100 100"
              >
                {[-30, -15, 0, 15, 30].map((y) => (
                  <line
                    key={y}
                    x1="0"
                    y1={50 + y}
                    x2="100"
                    y2={50 + y}
                    stroke="white"
                    strokeWidth="0.5"
                  />
                ))}
                <line
                  x1="50"
                  y1="0"
                  x2="50"
                  y2="100"
                  stroke="white"
                  strokeWidth="0.5"
                />
              </svg>
            </div>

            {/* Prograde marker - uses Astro normal color */}
            <div
              style={{
                position: "absolute",
                width: "24px",
                height: "24px",
                transform: "translate(-50%, -50%)",
                transition: "all 0.2s",
                top: `${50 - pitch * 0.8}%`,
                left: `${50 + yaw * 0.3}%`,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                style={{ width: "100%", height: "100%" }}
              >
                <circle
                  cx="12"
                  cy="12"
                  r="8"
                  fill="none"
                  stroke={statusColors.normal}
                  strokeWidth="2"
                />
                <line
                  x1="12"
                  y1="4"
                  x2="12"
                  y2="8"
                  stroke={statusColors.normal}
                  strokeWidth="2"
                />
                <line
                  x1="12"
                  y1="16"
                  x2="12"
                  y2="20"
                  stroke={statusColors.normal}
                  strokeWidth="2"
                />
                <line
                  x1="4"
                  y1="12"
                  x2="8"
                  y2="12"
                  stroke={statusColors.normal}
                  strokeWidth="2"
                />
                <line
                  x1="16"
                  y1="12"
                  x2="20"
                  y2="12"
                  stroke={statusColors.normal}
                  strokeWidth="2"
                />
              </svg>
            </div>

            {/* Target marker (if aligned) */}
            {attitude.targetDirection && (
              <div
                style={{
                  position: "absolute",
                  width: "20px",
                  height: "20px",
                  transform: "translate(-50%, -50%)",
                  top: "35%",
                  left: "65%",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    border: `2px solid ${tokens.colors.accent.primary}`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "6px",
                      height: "6px",
                      backgroundColor: tokens.colors.accent.primary,
                      borderRadius: "50%",
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Fixed crosshair (boresight) */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                color: statusColors.caution,
                fontSize: tokens.typography.fontSize.xxl,
                textShadow: `0 0 4px ${statusColors.caution}`,
              }}
            >
              ⨁
            </div>
          </div>

          {/* Yaw indicator arc */}
          <svg
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
            viewBox="0 0 100 100"
          >
            <path
              d="M 20 90 A 40 40 0 0 1 80 90"
              fill="none"
              stroke={`${tokens.colors.text.tertiary}40`}
              strokeWidth="2"
            />
            {/* Yaw needle */}
            <line
              x1="50"
              y1="85"
              x2={50 + Math.sin((yaw * Math.PI) / 180) * 25}
              y2={90 - Math.cos((yaw * Math.PI) / 180) * 10}
              stroke={tokens.colors.status.caution}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      {/* Attitude readouts - Astro: tabular numbers */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: tokens.spacing.md,
          fontFamily: tokens.typography.fontFamily.mono,
          fontSize: compact ? "10px" : tokens.typography.fontSize.xs,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span style={{ color: statusColors.critical }}>
          {formatAngle(roll, "R")}
        </span>
        <span style={{ color: statusColors.normal }}>
          {formatAngle(pitch, "P")}
        </span>
        <span style={{ color: statusColors.standby }}>
          {formatAngle(yaw, "Y")}
        </span>
      </div>

      {/* Target info - Astro: Sentence-case */}
      {!compact && targetName && (
        <div
          style={{
            marginTop: tokens.spacing.md,
            paddingTop: tokens.spacing.md,
            borderTop: `1px solid ${tokens.colors.border.muted}`,
            fontSize: tokens.typography.fontSize.xs,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: tokens.colors.text.tertiary }}>Target</span>
            <span
              style={{ color: safeAccentText(tokens.colors.accent.primary) }}
            >
              {targetName}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NavBallCard;
